// src/pages/Chat/PawChat.jsx
import React, { useState, useEffect, useRef } from "react";
import { Form, Button } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import "./PawChat.css";
import "../../styles/ChatTheme.css";
import {
  faPaperPlane,
  faPaw,
  faMicrochip,
  faHeart,
  faVolumeHigh,
  faVolumeXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FloatingPawButton from "../../components/FloatingPawButton";

const PAW_LAMBDA_URL =
  "https://tiwqg7ei5lnwvl2o3oudz26z7q0vzzwc.lambda-url.us-east-1.on.aws/";

// 🧹 Remove emojis & symbols that sound awkward in TTS
const stripEmojis = (text = "") =>
  String(text)
    .replace(
      /[\p{Extended_Pictographic}\p{Emoji_Presentation}\p{Emoji}\u200d]/gu,
      ""
    )
    .replace(/\s+/g, " ")
    .trim();

export default function PawChat() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "care"; // adoption | smarttag | care

  const [conversation, setConversation] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pawLoading, setPawLoading] = useState(true);

  // 🔽 bottom sentinel
  const chatEndRef = useRef(null);
  const scrollToBottom = (behavior = "auto") => {
    if (!chatEndRef.current) return;
    chatEndRef.current.scrollIntoView({ behavior, block: "end" });
  };
  useEffect(() => {
    scrollToBottom("auto");
  }, [conversation.length, loading]);

  // ============================
  // 🔊 Voice (Polly)
  // ============================
  const [voiceEnabled, setVoiceEnabled] = useState(false); // user choice
  const voicePrimedRef = useRef(false);
  const lastSpokenAiIndexRef = useRef(-1);

  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);

  // queue: { b64, vol }
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);

  // keep latest conversation for async speaking
  const convoRef = useRef([]);
  useEffect(() => {
    convoRef.current = conversation;
  }, [conversation]);

  const stopVoice = () => {
    try {
      audioQueueRef.current = [];
      isPlayingRef.current = false;
      voicePrimedRef.current = false;

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    } catch {}
  };

  const playNextFromQueue = () => {
    if (isPlayingRef.current) return;
    const item = audioQueueRef.current.shift();
    if (!item?.b64) return;

    const nextBase64 = item.b64;
    const vol = typeof item.vol === "number" ? item.vol : 1;

    isPlayingRef.current = true;

    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }

      const bytes = Uint8Array.from(atob(nextBase64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      audioUrlRef.current = url;

      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = url;
      audioRef.current.volume = vol;

      audioRef.current.onended = () => {
        try {
          if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
          }
        } catch {}
        isPlayingRef.current = false;
        playNextFromQueue();
      };

      audioRef.current.play().catch(() => {
        isPlayingRef.current = false;
        playNextFromQueue();
      });
    } catch {
      isPlayingRef.current = false;
      playNextFromQueue();
    }
  };

  const playPollyBase64 = (audioBase64, vol = 1) => {
    if (!audioBase64) return;
    audioQueueRef.current.push({ b64: audioBase64, vol });
    playNextFromQueue();
  };

  const fetchPollyForText = async (text, force = false) => {
    if (!force && !voiceEnabled) return null;

    const cleanText = stripEmojis(text || "");
    if (!cleanText) return null;

    try {
      const res = await fetch(PAW_LAMBDA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "tts",
          userMessage: cleanText,
          voice: true,
        }),
      });

      if (!res.ok) return null;
      const data = await res.json().catch(() => ({}));
      return data.audioBase64 || null;
    } catch {
      return null;
    }
  };

  // One-time silent prime (must be triggered by a user gesture)
  const primeVoice = async (force = false) => {
    if (!force && !voiceEnabled) return;
    if (voicePrimedRef.current) return;

    try {
      const b64 = await fetchPollyForText("Voice enabled.", true);
      if (!b64) return;

      playPollyBase64(b64, 0); // silent prime
      voicePrimedRef.current = true;
    } catch {}
  };

  // Speak last AI message immediately (when turning on)
  const speakLastAiNow = async (force = false) => {
    const list = convoRef.current || [];
    if (!list.length) return;

    const lastAiIndex = [...list].map((m) => m.sender).lastIndexOf("ai");
    if (lastAiIndex === -1) return;

    lastSpokenAiIndexRef.current = lastAiIndex;

    const b64 = await fetchPollyForText(list[lastAiIndex].text, force);
    if (b64) playPollyBase64(b64, 1);
  };

  // Speak newest AI message as it arrives (while voice is on)
  useEffect(() => {
    if (!voiceEnabled) return;
    if (!voicePrimedRef.current) return;

    const list = convoRef.current || [];
    if (!list.length) return;

    const lastAiIndex = [...list].map((m) => m.sender).lastIndexOf("ai");
    if (lastAiIndex === -1) return;

    if (lastAiIndex <= lastSpokenAiIndexRef.current) return;
    lastSpokenAiIndexRef.current = lastAiIndex;

    (async () => {
      const b64 = await fetchPollyForText(list[lastAiIndex].text);
      if (b64) playPollyBase64(b64, 1);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.length, voiceEnabled]);

  // Greeting based on mode + initial Paw loader
  useEffect(() => {
    const greetings = {
      care: [
        "Hi there! 🐾 I’m Paw, your pet-care companion.",
        "Ask me anything about training, health, or nutrition!",
      ],
      adoption: [
        "Welcome to the Adoption Assistant 🐶",
        "Tell me what kind of pet you’re looking for — I’ll help you find your match!",
      ],
      smarttag: [
        "Hello again! 🐕 Let’s manage or troubleshoot your SmartTag.",
        "What would you like help with today?",
      ],
    };

    setConversation([]);
    setPawLoading(true);

    lastSpokenAiIndexRef.current = -1;

    const timer = setTimeout(() => {
      setConversation(greetings[mode].map((text) => ({ sender: "ai", text })));
      setPawLoading(false);
    }, 700);

    return () => clearTimeout(timer);
  }, [mode]);

  // 🚫 Basic content filter
  function isBlockedMessage(raw) {
    if (!raw) return false;
    const text = raw.toLowerCase();

    const sexualKeywords = [
      "sex",
      "sext",
      "porn",
      "xxx",
      "onlyfans",
      "nsfw",
      "nude",
      "nud3",
      "sex joke",
      "erotic",
    ];

    const badWords = ["kill myself", "suicide", "kys", "hate you"];

    const blockedLists = [sexualKeywords, badWords];

    return blockedLists.some((list) => list.some((kw) => text.includes(kw)));
  }

  const handleSend = async () => {
    if (!input.trim()) return;

    const userText = input.trim();
    const userMsg = { sender: "user", text: userText };

    setConversation((prev) => [...prev, userMsg]);
    setInput("");

    if (isBlockedMessage(userText)) {
      setConversation((prev) => [
        ...prev,
        {
          sender: "ai",
          text:
            "I’m here to help with pets, adoption, and SmartTags — not that topic. 🐾\n" +
            "You can ask me about health, appointments, training, or finding a new home for a pet.",
        },
      ]);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(PAW_LAMBDA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userMessage: userText,
          mode,
        }),
      });

      const data = await response.json();
      const aiMsg = { sender: "ai", text: data.reply || "..." };
      setConversation((prev) => [...prev, aiMsg]);
    } catch (err) {
      console.error("Chat error:", err);
      setConversation((prev) => [
        ...prev,
        { sender: "ai", text: "Sorry, something went wrong." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const icon =
    mode === "smarttag" ? faMicrochip : mode === "adoption" ? faHeart : faPaw;

  const subtitle =
    mode === "smarttag"
      ? "SmartTag Setup & Help"
      : mode === "adoption"
      ? "Pet Adoption Assistant"
      : "Pet Care Companion";

  return (
    <div className="pawchat-wrapper">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="pawchat-shell"
      >
        {/* Header – app style */}
        <header className="pawchat-header">
          <div className="pawchat-header-icon">
            <FontAwesomeIcon icon={icon} />
          </div>

          <div className="pawchat-header-text">
            <h4 className="pawchat-title">Paw AI Assistant</h4>
            <p className="pawchat-subtitle">{subtitle}</p>
          </div>


        </header>

        {/* Messages */}
        <div className="chat-box pawchat-chat-box">
          {pawLoading && conversation.length === 0 && (
            <div className="paw-loading-container">
              <div className="paw-print">🐾</div>
              <div className="paw-print">🐾</div>
              <div className="paw-print">🐾</div>
              <div className="paw-loading-text">Paw is getting ready…</div>
            </div>
          )}

          {conversation.map((msg, i) => (
            <div
              key={i}
              className={`chat-bubble ${
                msg.sender === "ai" ? "ai-bubble" : "user-bubble"
              }`}
            >
              {msg.text}
            </div>
          ))}

          {loading && (
            <div className="chat-bubble ai-bubble paw-typing-wrapper">
              <span className="paw-typing-label">Paw is thinking</span>
              <div className="paw-typing-indicator">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </div>
            </div>
          )}

          {/* Spacer so last bubble clears the input bar on iOS */}
          <div style={{ height: "4.75rem" }} />

          <div ref={chatEndRef} />
        </div>

        {/* ✅ Input bar – docked at bottom, mobile-friendly voice button */}
      <Form
  className="chat-ui chat-input-row"
  onSubmit={(e) => {
    e.preventDefault();
    handleSend();
  }}
>
  <div className="lpp-input-bar-container">
    <div className="chat-input-pill">
      <div className="d-flex align-items-center gap-2 w-100">
        <Button
          size="sm"
          className={`lpp-voice-btn ${voiceEnabled ? "is-on" : "is-off"}`}
          onClick={async () => {
            const next = !voiceEnabled;
            setVoiceEnabled(next);

            if (!next) {
              stopVoice();
              voicePrimedRef.current = false;
              lastSpokenAiIndexRef.current = -1;
              return;
            }

            voicePrimedRef.current = false;
            lastSpokenAiIndexRef.current = -1;

            await primeVoice(true);
            await speakLastAiNow(true);
          }}
          title="Toggle voice"
          aria-label={voiceEnabled ? "Voice on" : "Voice off"}
          type="button"
        >
          <FontAwesomeIcon icon={voiceEnabled ? faVolumeHigh : faVolumeXmark} />
        </Button>

        <Form.Control
          type="text"
          placeholder="Type your message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="chat-input-field"
        />

        <button type="submit" disabled={loading} className="chat-send-icon-btn">
          <FontAwesomeIcon icon={faPaperPlane} />
        </button>
           {/* ✅ Floating Paw button OUTSIDE the form, pinned nicely */}

      </div>

    </div>

  </div>
</Form>
 <div className="">
          <FloatingPawButton isAuthenticated={true} hasActiveSub={true} />
        </div>


      </motion.div>
    </div>
  );
}
